<script lang="ts" setup>
import { RevisionAnnotation } from "types/RevisionAnnotation";

const props = defineProps({
  annotation: {
    type: Object as PropType<RevisionAnnotation>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const documentId = inject("documentId") as any;

const toggleAnnotationFlag = () => {
  props.annotation.shouldDeductMechanics =
    !props.annotation.shouldDeductMechanics;
  useSaveAnnotation(documentId).saveAnnotation(props.annotation);
};

const isAnnotationFlagged = computed(() => {
  return props.annotation.shouldDeductMechanics ?? false;
});

const label = computed(() => {
  return isAnnotationFlagged.value ? "restore 1pt" : "deduct 1pt";
});

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { hasMechanicsSummary } = storeToRefs(documentChecklistStore);

const isDisabled = computed(() => {
  return props.disabled || hasMechanicsSummary.value != true;
});

let showContainerTimeout: ReturnType<typeof setTimeout> | undefined;
let hideContainerTimeout: ReturnType<typeof setTimeout> | undefined;
let hoveredLineTop: number | undefined;

const keepContainerOpen = () => {
  clearTimeout(hideContainerTimeout);
};

const updateContainerPosition = (ev: MouseEvent) => {
  const wrapper = wrapperContainer.value;
  if (!wrapper || deductionContainerRef.value?.contains(ev.target as Node)) return;

  // A text range includes each wrapped line, even in a flex annotation wrapper.
  const range = document.createRange();
  range.selectNodeContents(wrapper);
  const line = Array.from(range.getClientRects()).find(
    (rect) => ev.clientY >= rect.top && ev.clientY <= rect.bottom
  );
  if (!line) return;
  // Once visible, keep the button steady as the pointer travels toward it.
  // Hovering another wrapped line still moves it to that line.
  if (showDeductionContainer.value && hoveredLineTop === line.top) return;
  hoveredLineTop = line.top;

  deductionContainerCoordinates.value = {
    x: Math.max(0, Math.min(ev.clientX - width / 2, window.innerWidth - width)),
    y: Math.max(0, line.top - height),
  };
};

const onMouseEnter = (ev: MouseEvent) => {
  keepContainerOpen();
  if (isDisabled.value) {
    return;
  }

  updateContainerPosition(ev);
  clearTimeout(showContainerTimeout);
  showContainerTimeout = setTimeout(() => {
    showDeductionContainer.value = true;
  }, 700);
};

const onMouseLeave = () => {
  clearTimeout(showContainerTimeout);
  keepContainerOpen();
  // Allow the pointer to cross the space between the text and the button.
  hideContainerTimeout = setTimeout(() => {
    showDeductionContainer.value = false;
  }, 200);
};

const showDeductionContainer = ref(false);
const deductionContainerRef = ref<HTMLElement>();
const wrapperContainer = ref<HTMLElement>();
const deductionContainerCoordinates = ref({ x: 0, y: 0 });
const width = 150;
const height = 40;

onUnmounted(() => {
  clearTimeout(showContainerTimeout);
  clearTimeout(hideContainerTimeout);
});
</script>

<template>
  <span
    ref="wrapperContainer"
    class="inline"
    @mouseenter="onMouseEnter"
    @mousemove="updateContainerPosition"
    @mouseleave="onMouseLeave"
  >
    <slot />
    <Teleport to="body">
      <div
        v-if="showDeductionContainer"
        ref="deductionContainerRef"
        class="fixed z-50 flex items-center justify-center bg-surface rounded shadow"
        @mouseenter="keepContainerOpen"
        @mouseleave="onMouseLeave"
        :style="{
          top: `${deductionContainerCoordinates.y}px`,
          left: `${deductionContainerCoordinates.x}px`,
          width: `${width}px`,
          height: `${height}px`,
        }"
      >
        <BaseTextButton
          class="h-full w-full"
          @click.stop.prevent="toggleAnnotationFlag"
        >
          <span
            :class="{
              ' text-success-default': isAnnotationFlagged,
              ' text-danger-default': !isAnnotationFlagged,
            }"
          >
            {{ label }}
          </span>
        </BaseTextButton>
      </div>
    </Teleport>
  </span>
</template>

<style scoped></style>
